import { useEffect, useState } from 'react';

interface ServiceWorkerUpdate {
  hasUpdate: boolean;
  waitingWorker: ServiceWorker | null;
}

export function useServiceWorker() {
  const [update, setUpdate] = useState<ServiceWorkerUpdate>({
    hasUpdate: false,
    waitingWorker: null,
  });

  useEffect(() => {
    if (import.meta.env.PROD && 'serviceWorker' in navigator) {
      registerServiceWorker();
    }
  }, []);

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      if (registration.waiting) {
        setUpdate({
          hasUpdate: true,
          waitingWorker: registration.waiting,
        });
      }

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdate({
                hasUpdate: true,
                waitingWorker: newWorker,
              });
            }
          });
        }
      });

      const safeUpdate = () => {
        registration.update().catch((err) => {
          if (import.meta.env.DEV) {
            console.warn('[SW] Update check failed:', err.message);
          }
        });
      };

      setInterval(safeUpdate, 60 * 60 * 1000);
      safeUpdate();

    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[SW] Registration failed:', err instanceof Error ? err.message : 'Unknown error');
      }
    }
  };

  const updateApp = () => {
    if (update.waitingWorker) {
      update.waitingWorker.postMessage({ type: 'SKIP_WAITING' });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        window.location.reload();
      });
    }
  };

  const clearCache = async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });

      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  };

  return {
    hasUpdate: update.hasUpdate,
    updateApp,
    clearCache,
  };
}
